import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'sbl_cart_v1';

// A cart line is uniquely identified by product + color + size + school, so
// the same shirt in a different size is a separate line, not a quantity bump
// on the same one. School is part of the key because the same tote for two
// different schools is two different physical products, even though it's the
// same Stripe price.
function lineKey({ productId, colorKey, size, schoolId }) {
  return `${productId}::${colorKey}::${size || 'default'}::${schoolId || 'none'}`;
}

function loadInitialState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { items: [] };
  } catch {
    return { items: [] };
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const key = lineKey(action.payload);
      const existing = state.items.find((i) => i.key === key);
      if (existing) {
        return {
          items: state.items.map((i) =>
            i.key === key ? { ...i, quantity: i.quantity + action.payload.quantity } : i
          ),
        };
      }
      return { items: [...state.items, { ...action.payload, key, quantity: action.payload.quantity }] };
    }
    case 'UPDATE_QUANTITY': {
      const { key, quantity } = action.payload;
      if (quantity <= 0) {
        return { items: state.items.filter((i) => i.key !== key) };
      }
      return { items: state.items.map((i) => (i.key === key ? { ...i, quantity } : i)) };
    }
    case 'REMOVE_ITEM':
      return { items: state.items.filter((i) => i.key !== action.payload.key) };
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitialState);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage unavailable (private browsing, etc.) — cart just
      // won't persist across reloads; not worth blocking the UI over.
    }
  }, [state]);

  const value = useMemo(() => {
    const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = state.items.reduce((sum, i) => sum + i.quantity * (i.price || 0), 0);

    return {
      items: state.items,
      itemCount,
      subtotal,
      addItem: (item) => dispatch({ type: 'ADD_ITEM', payload: item }),
      updateQuantity: (key, quantity) => dispatch({ type: 'UPDATE_QUANTITY', payload: { key, quantity } }),
      removeItem: (key) => dispatch({ type: 'REMOVE_ITEM', payload: { key } }),
      clear: () => dispatch({ type: 'CLEAR' }),
    };
  }, [state]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside a <CartProvider>');
  return ctx;
}
